/**
 * Minimal ZIP container for the surgical OOXML writer — V103-7-2 (spike
 * V103-7-1, docs/v10.3/xlsx-spike.md).
 *
 *   - `readZip` reads the central directory and returns each entry with its
 *     STILL-COMPRESSED bytes, so an untouched entry is copied byte for byte.
 *   - `writeZip` lays entries out again (local header, data, central
 *     directory) and returns the output as a list of chunks: nothing is
 *     concatenated, so a 12 MB ConMon matrix never needs a second full copy.
 *   - Zip64, encrypted entries and multi-disk archives are refused: GovRAMP's
 *     templates are far below 4 GB, and anything else is not a template.
 *
 * Pure: fflate (MIT) + Web APIs only. No Node built-ins, no Cloudflare imports.
 */
import { inflateSync, deflateSync, strFromU8, strToU8 } from 'fflate';
export class ZipFormatError extends Error {
    code = 'template_mismatch';
    constructor(message) {
        super(message);
        this.name = 'ZipFormatError';
    }
}
const CRC_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++)
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    return t;
})();
/** Incremental CRC-32 (start with 0). */
export function crc32Update(crc, buf) {
    let c = crc ^ 0xffffffff;
    for (let i = 0; i < buf.length; i++)
        c = CRC_TABLE[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}
const MAX_32 = 0xffffffff;
/** Parse a ZIP archive's central directory. Throws ZipFormatError on anything that isn't a plain archive. */
export function readZip(u8) {
    if (u8.length < 22)
        throw new ZipFormatError('Not a ZIP archive.');
    const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
    let eocd = -1;
    for (let i = u8.length - 22; i >= Math.max(0, u8.length - 65_557); i--) {
        if (dv.getUint32(i, true) === 0x06054b50) {
            eocd = i;
            break;
        }
    }
    if (eocd < 0)
        throw new ZipFormatError('Not a ZIP archive.');
    if (dv.getUint16(eocd + 4, true) !== 0 || dv.getUint16(eocd + 6, true) !== 0)
        throw new ZipFormatError('Multi-disk ZIP archives are not supported.');
    const n = dv.getUint16(eocd + 10, true);
    let p = dv.getUint32(eocd + 16, true);
    if (n === 0xffff || p === MAX_32)
        throw new ZipFormatError('Zip64 archives are not supported.');
    const entries = [];
    for (let i = 0; i < n; i++) {
        if (p + 46 > u8.length || dv.getUint32(p, true) !== 0x02014b50)
            throw new ZipFormatError('Corrupt ZIP central directory.');
        const flags = dv.getUint16(p + 8, true);
        const method = dv.getUint16(p + 10, true);
        const csize = dv.getUint32(p + 20, true);
        const usize = dv.getUint32(p + 24, true);
        const nlen = dv.getUint16(p + 28, true);
        const xlen = dv.getUint16(p + 30, true);
        const clen = dv.getUint16(p + 32, true);
        const lho = dv.getUint32(p + 42, true);
        if (csize === MAX_32 || usize === MAX_32 || lho === MAX_32)
            throw new ZipFormatError('Zip64 archives are not supported.');
        if (flags & 1)
            throw new ZipFormatError('Encrypted ZIP entries are not supported.');
        if (method !== 0 && method !== 8)
            throw new ZipFormatError(`Unsupported ZIP compression method ${method}.`);
        const name = strFromU8(u8.subarray(p + 46, p + 46 + nlen));
        if (lho + 30 > u8.length || dv.getUint32(lho, true) !== 0x04034b50)
            throw new ZipFormatError(`Corrupt local header for ${name}.`);
        const dataStart = lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
        if (dataStart + csize > u8.length)
            throw new ZipFormatError(`Truncated ZIP entry ${name}.`);
        entries.push({
            name, method, time: dv.getUint16(p + 12, true), date: dv.getUint16(p + 14, true),
            crc: dv.getUint32(p + 16, true), csize, usize, data: u8.subarray(dataStart, dataStart + csize),
        });
        p += 46 + nlen + xlen + clen;
    }
    return entries;
}
/** The uncompressed bytes of one entry. */
export function inflateEntry(e) {
    const data = Array.isArray(e.data) ? concatChunks(e.data) : e.data;
    if (e.method === 0)
        return data;
    return inflateSync(data, { out: new Uint8Array(e.usize) });
}
export function concatChunks(chunks) {
    const total = chunks.reduce((a, c) => a + c.length, 0);
    const out = new Uint8Array(total);
    let o = 0;
    for (const c of chunks) {
        out.set(c, o);
        o += c.length;
    }
    return out;
}
/** A replacement entry with new (deflated) XML content; keeps the original name and timestamps. */
export function replacedEntry(entry, text) {
    return deflatedEntry(entry.name, strToU8(text), { time: entry.time, date: entry.date });
}
/** A new entry: deflated (level 6) or stored. */
export function deflatedEntry(name, raw, at, store = false) {
    if (store)
        return { name, method: 0, time: at.time, date: at.date, crc: crc32Update(0, raw), usize: raw.length, csize: raw.length, data: raw };
    const data = deflateSync(raw, { level: 6 });
    return { name, method: 8, time: at.time, date: at.date, crc: crc32Update(0, raw), usize: raw.length, csize: data.length, data };
}
/** A stored entry whose bytes are already a list of chunks (e.g. an XLSX inside a bundle). */
export function storedChunksEntry(name, chunks, at) {
    let crc = 0;
    let size = 0;
    for (const c of chunks) {
        crc = crc32Update(crc, c);
        size += c.length;
    }
    return { name, method: 0, time: at.time, date: at.date, crc, usize: size, csize: size, data: chunks };
}
/** DOS date/time for a UTC instant (2-second resolution; 1980 floor). */
export function dosDateTime(d) {
    const y = Math.max(1980, d.getUTCFullYear());
    return {
        time: (d.getUTCHours() << 11) | (d.getUTCMinutes() << 5) | Math.floor(d.getUTCSeconds() / 2),
        date: ((y - 1980) << 9) | ((d.getUTCMonth() + 1) << 5) | d.getUTCDate(),
    };
}
/** Lay the entries out as a ZIP archive; returns the output chunks in order. */
export function writeZip(entries) {
    const chunks = [];
    const central = [];
    let offset = 0;
    if (entries.length >= 0xffff)
        throw new ZipFormatError('Too many ZIP entries.');
    for (const e of entries) {
        const name = strToU8(e.name);
        const dataChunks = Array.isArray(e.data) ? e.data : [e.data];
        const dataLen = dataChunks.reduce((a, c) => a + c.length, 0);
        if (dataLen !== e.csize)
            throw new ZipFormatError(`Entry ${e.name}: data length ${dataLen} ≠ csize ${e.csize}.`);
        if (offset + 30 + name.length + dataLen >= MAX_32 || e.usize >= MAX_32)
            throw new ZipFormatError('Output would need Zip64.');
        const lh = new Uint8Array(30 + name.length);
        const v = new DataView(lh.buffer);
        v.setUint32(0, 0x04034b50, true);
        v.setUint16(4, 20, true);
        v.setUint16(6, 0x0800, true); // UTF-8 names, sizes in the local header (no data descriptor)
        v.setUint16(8, e.method, true);
        v.setUint16(10, e.time, true);
        v.setUint16(12, e.date, true);
        v.setUint32(14, e.crc, true);
        v.setUint32(18, e.csize, true);
        v.setUint32(22, e.usize, true);
        v.setUint16(26, name.length, true);
        lh.set(name, 30);
        const ch = new Uint8Array(46 + name.length);
        const cv = new DataView(ch.buffer);
        cv.setUint32(0, 0x02014b50, true);
        cv.setUint16(4, 20, true);
        cv.setUint16(6, 20, true);
        cv.setUint16(8, 0x0800, true);
        cv.setUint16(10, e.method, true);
        cv.setUint16(12, e.time, true);
        cv.setUint16(14, e.date, true);
        cv.setUint32(16, e.crc, true);
        cv.setUint32(20, e.csize, true);
        cv.setUint32(24, e.usize, true);
        cv.setUint16(28, name.length, true);
        cv.setUint32(42, offset, true);
        ch.set(name, 46);
        central.push(ch);
        chunks.push(lh, ...dataChunks);
        offset += lh.length + dataLen;
    }
    const cdSize = central.reduce((a, c) => a + c.length, 0);
    const end = new Uint8Array(22);
    const ev = new DataView(end.buffer);
    ev.setUint32(0, 0x06054b50, true);
    ev.setUint16(8, entries.length, true);
    ev.setUint16(10, entries.length, true);
    ev.setUint32(12, cdSize, true);
    ev.setUint32(16, offset, true);
    return [...chunks, ...central, end];
}
export { strFromU8, strToU8 };
//# sourceMappingURL=zip.js.map